import axios, { type AxiosInstance } from 'axios'
import { config as globalConfig } from '@/config/env'

const apiClient: AxiosInstance = axios.create({
  baseURL: globalConfig.apiBaseUrl,
})

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('asl_access_token')
    const org_id = JSON.parse(localStorage.getItem('asl_current_organization') || '{}')?.id
    const user = JSON.parse(localStorage.getItem('asl_user') || '{}')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    if (user.role === 'superadmin' && org_id) {
      config.headers['X-Organization-ID'] = org_id
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// ---------------------------------------------------------------------------
// TEK TAZELEME (single-flight) + TEK YONLENDIRME
//
// ESKI HATA: ayni anda gelen HER 401 kendi basina /auth/refresh/ cagiriyordu.
// Backend'de ROTATE_REFRESH_TOKENS ve BLACKLIST_AFTER_ROTATION acik; ilk
// tazeleme eski refresh token'i kara listeye atiyor, paralel giden digerleri
// 401 aliyor ve her biri ayri ayri tarayiciyi /login sayfasina gonderiyordu.
// Sayfa yeniden yuklenince uygulama yine 5-6 istek atiyor ve dongu basliyordu:
// giris ekraninda saniyede birkac kez yanip sonme.
//
// Cozum: tazeleme sozu (promise) paylasilir - kac istek 401 alirsa alsin
// /auth/refresh/ BIR kez cagrilir, digerleri ayni sonucu bekler. Basarisiz
// olursa giris sayfasina EN FAZLA BIR KEZ gidilir.
// ---------------------------------------------------------------------------
let tazelemeSozu: Promise<string> | null = null
let yonlendirildi = false

function oturumuKapat(): void {
  localStorage.removeItem('asl_access_token')
  localStorage.removeItem('asl_refresh_token')
  if (yonlendirildi) return
  if (window.location.pathname.startsWith('/login')) return
  yonlendirildi = true
  window.location.href = '/login'
}

function tokenTazele(): Promise<string> {
  if (tazelemeSozu) return tazelemeSozu

  const refreshToken = localStorage.getItem('asl_refresh_token')
  if (!refreshToken) {
    return Promise.reject(new Error('ASL_REFRESH_YOK'))
  }

  tazelemeSozu = axios
    .post(`${globalConfig.apiBaseUrl}/auth/refresh/`, { refresh: refreshToken })
    .then((rs) => {
      const { access, refresh } = rs.data
      localStorage.setItem('asl_access_token', access)
      if (refresh) {
        localStorage.setItem('asl_refresh_token', refresh)
      }
      return access as string
    })
    .finally(() => {
      tazelemeSozu = null
    })

  return tazelemeSozu
}

apiClient.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalConfig = err.config
    if (!originalConfig || !err.response) {
      return Promise.reject(err)
    }

    const url: string = originalConfig.url || ''
    const kimlikYolu = url.includes('/auth/login') || url.includes('/auth/refresh')

    if (err.response.status === 401 && !kimlikYolu && !originalConfig._retry) {
      originalConfig._retry = true
      try {
        const yeniToken = await tokenTazele()
        originalConfig.headers = originalConfig.headers || {}
        originalConfig.headers.Authorization = `Bearer ${yeniToken}`
        return apiClient(originalConfig)
      } catch (_error: any) {
        // Oturumu SADECE gercek kimlik hatasinda kapat.
        // Ag kopuklugu veya 5xx'te kullaniciyi disari atma.
        const kod = _error?.response?.status
        const tokenYok = _error?.message === 'ASL_REFRESH_YOK'
        if (tokenYok || (kod && kod >= 400 && kod < 500)) {
          oturumuKapat()
        }
        return Promise.reject(_error)
      }
    }

    return Promise.reject(err)
  }
)

// (03.11.2026) sunucu mesajini goster: hata bildiriminde "status code 400" yerine sunucunun Rusca aciklamasi
apiClient.interceptors.response.use(
  (r) => r,
  (e: any) => {
    const d = e?.response?.data
    const m = d && typeof d === 'object' ? (d.error || d.detail || d.message) : null
    if (typeof m === 'string' && m.trim()) e.message = m
    return Promise.reject(e)
  },
)

export default apiClient
